import { useLayoutEffect, useRef, useState } from 'react'
import { sfxClick } from '../gameslib/audio'

const NO_HAND = new Set(['watch', 'listen'])

/** Material Symbols Outlined — https://fonts.google.com/icons?icon.query=hand */
const GESTURE_ICON = {
  hold: 'front_hand',
  tap: 'touch_app',
  drag: 'swipe_vertical',
  slide: 'swipe',
  move: 'pan_tool_alt',
  rotate: 'back_hand',
  'axis-x': 'swipe',
  'axis-y': 'swipe_vertical',
}

/**
 * Subtle coach overlay. Hand sits on the real control when targetRef/targetEl is set.
 * Memorize/listen = caption only. Uses Material Symbols hand/gesture icons.
 */
export function TutorialCoach({
  message,
  gesture = null,
  anchor = 'center',
  onSkip,
  targetRef = null,
  targetEl = null,
  slideAxis = null,
}) {
  const rootRef = useRef(null)
  const [pin, setPin] = useState(null)

  const resolved =
    gesture === 'slide' && slideAxis === 'y'
      ? 'axis-y'
      : gesture === 'slide' && slideAxis === 'x'
        ? 'axis-x'
        : gesture

  const showHand = Boolean(gesture) && !NO_HAND.has(gesture)
  const iconName = (resolved && GESTURE_ICON[resolved]) || GESTURE_ICON.tap
  const pinAbove = resolved === 'tap'

  useLayoutEffect(() => {
    if (!showHand) {
      setPin(null)
      return undefined
    }

    let highlighted = null
    let observed = null
    let boot = 0
    let raf = 0
    let cancelled = false

    const getTarget = () => targetEl || targetRef?.current || null

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => update()) : null

    const update = () => {
      if (cancelled) return
      const el = getTarget()
      const root = rootRef.current

      if (el !== highlighted) {
        highlighted?.classList.remove('is-tutor-target')
        highlighted = el
        highlighted?.classList.add('is-tutor-target')
      }

      if (el !== observed) {
        if (observed && ro) ro.unobserve(observed)
        observed = el
        if (el && ro) ro.observe(el)
      }

      if (!el || !root) {
        setPin(null)
        return
      }

      const er = el.getBoundingClientRect()
      const rr = root.getBoundingClientRect()
      if (er.width < 2 || er.height < 2) {
        setPin(null)
        return
      }

      // Tap: cluster sits just above the button (caption + hand pointing down)
      setPin({
        left: er.left + er.width / 2 - rr.left,
        top: pinAbove ? er.top - rr.top - 6 : er.top + er.height / 2 - rr.top,
        w: er.width,
        h: er.height,
        mode: pinAbove ? 'above' : 'center',
      })
    }

    const bootPin = () => {
      if (cancelled) return
      update()
      if (++boot < 8) raf = requestAnimationFrame(bootPin)
    }

    update()
    bootPin()

    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    const id = window.setInterval(update, 120)

    return () => {
      cancelled = true
      highlighted?.classList.remove('is-tutor-target')
      ro?.disconnect()
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
      window.clearInterval(id)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [showHand, targetRef, targetEl, resolved, message, pinAbove])

  if (!message && !gesture) return null

  const demoClass = [
    'tutor__demo',
    `tutor__demo--${resolved || 'tap'}`,
    pin ? 'is-pinned' : '',
    pin?.mode === 'above' ? 'is-above' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const clusterStyle = pin
    ? {
        left: pin.left,
        top: pin.top,
      }
    : undefined

  return (
    <div
      ref={rootRef}
      className={`tutor tutor--${anchor}${showHand ? '' : ' tutor--caption-only'}${pin ? ' tutor--pinned' : ''}${pin?.mode === 'above' ? ' tutor--pin-above' : ''}`}
      role="status"
      aria-live="polite"
    >
      {onSkip && (
        <button
          type="button"
          className="tutor__skip"
          onClick={() => {
            void sfxClick()
            onSkip()
          }}
        >
          Skip
        </button>
      )}

      {pin?.mode === 'above' ? (
        <div className="tutor__cluster tutor__cluster--above" style={clusterStyle} aria-hidden={!message}>
          <div className="tutor__caption">
            <p className="tutor__msg">{message}</p>
          </div>
          {showHand && (
            <div className={demoClass}>
              <span className={`material-symbols-outlined tutor__ms tutor__ms--${resolved || 'tap'}`}>
                {iconName}
              </span>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="tutor__caption">
            <p className="tutor__msg">{message}</p>
          </div>
          {showHand && (
            <div
              className={demoClass}
              style={
                pin
                  ? {
                      left: pin.left,
                      top: pin.top,
                    }
                  : undefined
              }
              aria-hidden
            >
              <span className={`material-symbols-outlined tutor__ms tutor__ms--${resolved || 'tap'}`}>
                {iconName}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  )
}
